import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./sidebar.css";

const MENU = [
  { to: "/", label: "DASHBOARD", icon: "bi-speedometer2" },
  { to: "/viewOrder", label: "VIEW ORDER", icon: "bi-receipt" },
  { to: "/newOrder", label: "NEW ORDER", icon: "bi-plus-circle" },
  { to: "/viewProduct", label: "VIEW PRODUCT", icon: "bi-box-seam" },
  { to: "/addProduct", label: "NEW PRODUCT", icon: "bi-bag-plus" },
  { to: "/viewPurchase", label: "VIEW PURCHASE", icon: "bi-cart-check" },
  { to: "/newPurchase", label: "NEW PURCHASE", icon: "bi-cart-plus" },
  { to: "/viewExecutive", label: "VIEW EXECUTIVE", icon: "bi-people" },
  { to: "/newExecutive", label: "NEW EXECUTIVE", icon: "bi-person-plus" },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // default: desktop open, mobile closed
  const [isSidebarOpen, setSidebarOpen] = useState(!(window.innerWidth <= 1024));

  // ✅ Default collapsed on desktop
  const [collapsed, setCollapsed] = useState(!(window.innerWidth <= 1024));

  const handleResize = () => {
    const mobile = window.innerWidth <= 1024;
    setIsMobile(mobile);
    setSidebarOpen(!mobile);

    // on mobile, don’t use collapsed mode (overlay should show labels)
    if (mobile) setCollapsed(false);

    // when returning to desktop, default collapsed again
    if (!mobile) setCollapsed(true);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen((v) => !v);

  const handleLogout = (e) => {
    e.preventDefault();
    sessionStorage.clear();
    localStorage.clear();
    navigate("/");
  };

  const onNavClick = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const sidebarClass = useMemo(() => {
    let cls = "app-sidebar border-end d-flex flex-column";
    cls += isSidebarOpen ? " open" : " closed";
    if (!isMobile && collapsed) cls += " is-collapsed";
    return cls;
  }, [isMobile, collapsed, isSidebarOpen]);

  return (
    <>
      {/* Mobile toggle */}
      {isMobile && (
        <button
          className="sidebar_toggle_btn btn btn-primary"
          onClick={toggleSidebar}
          type="button"
        >
          <i className="bi bi-list fs-4" />
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobile && isSidebarOpen && (
        <div className="sidebar_backdrop" onClick={toggleSidebar} />
      )}

      <aside className={sidebarClass}>
        {/* Top brand + desktop collapse toggle */}
        <div className="sidebar-top border-bottom d-flex align-items-center justify-content-between px-3">
          <div className="d-flex align-items-center gap-2">
            {(!collapsed || isMobile) && (
              <span className="fw-semibold">FASTBILLING</span>
            )}
          </div>

          {!isMobile && (
            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => setCollapsed((v) => !v)}
              aria-label="Collapse sidebar"
              title={collapsed ? "Expand" : "Collapse"}
            >
              <i className={`bi ${collapsed ? "bi-chevron-right" : "bi-chevron-left"}`} />
            </button>
          )}
        </div>

        {/* Menu */}
        <nav className="p-2">
          {MENU.map((item) => {
            const active = location.pathname === item.to;
            const iconOnly = !isMobile && collapsed;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavClick}
                className={[
                  "sidebar-link d-flex align-items-center rounded-3",
                  iconOnly ? "justify-content-center" : "",
                  active ? "active" : "",
                ].join(" ")}
                title={iconOnly ? item.label : undefined}
              >
                <i className={`bi ${item.icon} fs-5`} />
                {(!collapsed || isMobile) && (
                  <span className="ms-3 fw-semibold">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* ✅ Bottom only Logout */}
        <div className="mt-auto border-top p-2">
          <a
            href="#logout"
            onClick={(e) => {
              onNavClick();
              handleLogout(e);
            }}
            className={[
              "sidebar-link d-flex align-items-center rounded-3",
              (!isMobile && collapsed) ? "justify-content-center" : "",
            ].join(" ")}
            title={(!isMobile && collapsed) ? "LOGOUT" : undefined}
          >
            <i className="bi bi-box-arrow-right fs-4" />
            {(!collapsed || isMobile) && (
              <span className="ms-3 fw-semibold">LOGOUT</span>
            )}
          </a>
        </div>
      </aside>
    </>
  );
}